import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { axiosInstance } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import { ArrowBigUp, ArrowBigDown, ArrowLeft } from "lucide-react";

const Comment = ({ comment, user, onReply, onVote, onDelete, depth = 0 }) => {
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyContent, setReplyContent] = useState("");

  const handleReply = async () => {
    if (!replyContent.trim()) return;
    await onReply(comment.id, replyContent);
    setReplyContent("");
    setReplyOpen(false);
  };

  return (
    <div className={`${depth > 0 ? 'ml-8 mt-4' : 'mt-4'}`}>
      <Card className="bg-white/80 backdrop-blur-sm border-orange-50">
        <CardContent className="pt-4">
          <div className="flex gap-3">
            <div className="flex flex-col items-center gap-1">
              <Button 
                data-testid={`upvote-comment-${comment.id}`}
                variant="ghost" 
                size="sm" 
                className="p-1 h-auto hover:bg-orange-100"
                onClick={() => onVote(comment.id, "up")}
              >
                <ArrowBigUp className="w-5 h-5 text-gray-600 hover:text-orange-500" />
              </Button>
              <span className="text-xs font-bold text-gray-700">
                {comment.upvotes - comment.downvotes}
              </span>
              <Button 
                data-testid={`downvote-comment-${comment.id}`}
                variant="ghost" 
                size="sm" 
                className="p-1 h-auto hover:bg-blue-100"
                onClick={() => onVote(comment.id, "down")}
              >
                <ArrowBigDown className="w-5 h-5 text-gray-600 hover:text-blue-500" />
              </Button>
            </div>

            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Avatar className="w-6 h-6">
                  <AvatarImage src={comment.author_picture} />
                  <AvatarFallback>{comment.author_name?.[0]?.toUpperCase()}</AvatarFallback>
                </Avatar>
                <span className="font-medium text-sm">{comment.author_name}</span>
                <span className="text-xs text-gray-500">
                  {new Date(comment.created_at).toLocaleString()}
                </span>
              </div>
              <p className="text-gray-700 mb-3 whitespace-pre-wrap">{comment.content}</p>
              <div className="flex gap-2">
                <Button 
                  data-testid={`reply-comment-${comment.id}`}
                  variant="ghost" 
                  size="sm"
                  onClick={() => setReplyOpen(!replyOpen)}
                  className="text-xs"
                >
                  Reply
                </Button>
                {user.id === comment.author_id && (
                  <Button 
                    data-testid={`delete-comment-${comment.id}`}
                    variant="ghost" 
                    size="sm"
                    onClick={() => onDelete(comment.id)}
                    className="text-xs text-red-600 hover:text-red-700"
                  >
                    Delete
                  </Button>
                )}
              </div>

              {replyOpen && (
                <div className="mt-3 space-y-2">
                  <Textarea
                    data-testid={`reply-input-${comment.id}`}
                    placeholder="Write a reply..."
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    rows={3}
                    className="resize-none"
                  />
                  <div className="flex gap-2">
                    <Button 
                      data-testid={`submit-reply-${comment.id}`}
                      size="sm" 
                      onClick={handleReply}
                      className="bg-orange-500 hover:bg-orange-600"
                    >
                      Post Reply
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => {
                        setReplyOpen(false);
                        setReplyContent("");
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {comment.replies && comment.replies.length > 0 && (
        <div>
          {comment.replies.map((reply) => (
            <Comment
              key={reply.id}
              comment={reply}
              user={user}
              onReply={onReply}
              onVote={onVote}
              onDelete={onDelete}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const ThreadDetail = ({ user }) => {
  const { threadId } = useParams();
  const navigate = useNavigate();
  const [thread, setThread] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentContent, setCommentContent] = useState("");

  useEffect(() => {
    fetchThread();
    fetchComments();
  }, [threadId]);

  const fetchThread = async () => {
    try {
      const response = await axiosInstance.get(`/threads/${threadId}`);
      setThread(response.data);
    } catch (error) {
      toast.error("Failed to load thread");
      navigate("/app/threads");
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const response = await axiosInstance.get(`/threads/${threadId}/comments`);
      setComments(response.data);
    } catch (error) {
      toast.error("Failed to load comments");
    }
  };

  const handleVoteThread = async (voteType) => {
    try {
      await axiosInstance.post(`/threads/${threadId}/vote`, { vote_type: voteType });
      fetchThread();
    } catch (error) {
      toast.error("Failed to vote");
    }
  };

  const handleVoteComment = async (commentId, voteType) => {
    try {
      await axiosInstance.post(`/comments/${commentId}/vote`, { vote_type: voteType });
      fetchComments();
    } catch (error) {
      toast.error("Failed to vote");
    }
  };

  const handlePostComment = async () => {
    if (!commentContent.trim()) return;
    try {
      await axiosInstance.post(`/threads/${threadId}/comments`, { content: commentContent });
      setCommentContent("");
      toast.success("Comment posted!");
      fetchComments();
      fetchThread();
    } catch (error) {
      toast.error("Failed to post comment");
    }
  };

  const handleReply = async (parentCommentId, content) => {
    try {
      await axiosInstance.post(`/threads/${threadId}/comments`, {
        content,
        parent_comment_id: parentCommentId
      });
      toast.success("Reply posted!");
      fetchComments();
      fetchThread();
    } catch (error) {
      toast.error("Failed to post reply");
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm("Are you sure you want to delete this comment?")) return;
    try {
      await axiosInstance.delete(`/comments/${commentId}`);
      toast.success("Comment deleted");
      fetchComments();
      fetchThread();
    } catch (error) {
      toast.error("Failed to delete comment");
    }
  };

  const handleDeleteThread = async () => {
    if (!window.confirm("Are you sure you want to delete this thread?")) return;
    try {
      await axiosInstance.delete(`/threads/${threadId}`);
      toast.success("Thread deleted");
      navigate("/app/threads");
    } catch (error) {
      toast.error("Failed to delete thread");
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  if (!thread) {
    return null;
  }

  return (
    <div data-testid="thread-detail-page" className="max-w-4xl mx-auto">
      <Button 
        data-testid="back-to-threads"
        variant="ghost" 
        onClick={() => navigate("/app/threads")}
        className="mb-4"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Discussions
      </Button>

      <Card className="bg-white/90 backdrop-blur-sm border-orange-100">
        <div className="flex">
          <div className="flex flex-col items-center p-6 gap-2 bg-gray-50 rounded-l-lg">
            <Button 
              data-testid="upvote-thread"
              variant="ghost" 
              size="sm" 
              className="p-1 h-auto hover:bg-orange-100"
              onClick={() => handleVoteThread("up")}
            >
              <ArrowBigUp className="w-8 h-8 text-gray-600 hover:text-orange-500" />
            </Button>
            <span className="text-lg font-bold text-gray-700">
              {thread.upvotes - thread.downvotes}
            </span>
            <Button 
              data-testid="downvote-thread"
              variant="ghost" 
              size="sm" 
              className="p-1 h-auto hover:bg-blue-100"
              onClick={() => handleVoteThread("down")}
            >
              <ArrowBigDown className="w-8 h-8 text-gray-600 hover:text-blue-500" />
            </Button>
          </div>

          <div className="flex-1">
            <CardHeader>
              <CardTitle className="text-3xl mb-3" style={{ fontFamily: 'Merriweather, serif' }}>
                {thread.title}
              </CardTitle>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Avatar className="w-8 h-8">
                  <AvatarImage src={thread.author_picture} />
                  <AvatarFallback>{thread.author_name?.[0]?.toUpperCase()}</AvatarFallback>
                </Avatar>
                <span className="font-medium">{thread.author_name}</span>
                <span>•</span>
                <span>{new Date(thread.created_at).toLocaleString()}</span>
              </div>
            </CardHeader>
            <CardContent>
              {thread.image_url && (
                <div className="mb-4 rounded-lg overflow-hidden">
                  <img src={thread.image_url} alt={thread.title} className="w-full max-h-96 object-cover" />
                </div>
              )}
              <p className="text-gray-700 whitespace-pre-wrap mb-4">{thread.content}</p>
              {(user.id === thread.author_id || user.is_admin) && (
                <Button 
                  data-testid="delete-thread"
                  variant="destructive" 
                  onClick={handleDeleteThread}
                  className="rounded-full"
                >
                  Delete Thread
                </Button>
              )}
            </CardContent>
          </div>
        </div>
      </Card>

      <div className="mt-8">
        <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Merriweather, serif' }}>
          Comments ({thread.comment_count})
        </h2>

        <Card className="bg-white/90 backdrop-blur-sm border-orange-100 mb-6">
          <CardContent className="pt-6">
            <Textarea
              data-testid="main-comment-input"
              placeholder="Share your thoughts..."
              value={commentContent}
              onChange={(e) => setCommentContent(e.target.value)}
              rows={4}
              className="resize-none mb-3"
            />
            <Button 
              data-testid="post-comment-button"
              onClick={handlePostComment}
              className="bg-orange-500 hover:bg-orange-600 rounded-full"
            >
              Post Comment
            </Button>
          </CardContent>
        </Card>

        <div className="space-y-4">
          {comments.map((comment) => (
            <Comment
              key={comment.id}
              comment={comment}
              user={user}
              onReply={handleReply}
              onVote={handleVoteComment}
              onDelete={handleDeleteComment}
            />
          ))}
        </div>

        {comments.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No comments yet. Be the first to comment!
          </div>
        )}
      </div>
    </div>
  );
};

export default ThreadDetail;